<template>
  <div class="row">
    <div class="col-md-5 col-md-offset-3">
      <WdsHeading as="h1" type="title1">
        {{ $t('Change your password') }}
      </WdsHeading>

      <WdsAlert v-if="statusText !== ''" class="tw-mt-6">
        {{ statusText }}
      </WdsAlert>

      <WdsAlert class="tw-mt-6" info>
        <WdsText class="tw-mb-1">
          {{ $t('For a secure password, use 16 or more characters.') }}
        </WdsText>
        <WdsText>
          {{
            $t(
              'Shorter passwords (8–15 characters) must include at least one uppercase letter, one lowercase letter, and one number.',
            )
          }}
        </WdsText>
      </WdsAlert>

      <div class="tw-mt-6">
        <WdsInput
          v-model="currentPassword"
          name="old_password"
          type="password"
          :placeholder="$t('Old password')"
          autofocus
          autocomplete="current-password"
          required
          :error="
            errors.currentPassword.length === 0 ? undefined : errors.currentPassword.join('\n')
          "
          @update:model-value="resetErrors"
        >
          <template #label>
            {{ $t('Old password') }}
          </template>
        </WdsInput>

        <WdsInput
          v-model="password"
          name="password"
          type="password"
          :placeholder="$t('New password')"
          autocomplete="new-password"
          required
          :error="errors.password.length === 0 ? undefined : errors.password.join('\n')"
          @update:model-value="resetErrors"
        >
          <template #label>
            {{ $t('New password') }}
          </template>
        </WdsInput>

        <WdsInput
          v-model="passwordConfirmation"
          name="passwordConfirmation"
          type="password"
          :placeholder="$t('Confirm new password')"
          autocomplete="new-password"
          required
          :error="
            errors.passwordConfirmation.length === 0
              ? undefined
              : errors.passwordConfirmation.join('\n')
          "
          @update:model-value="resetErrors"
        >
          <template #label>
            {{ $t('Confirm new password') }}
          </template>
        </WdsInput>

        <div class="text-center tw-mt-6">
          <WdsButton primary full-width :disabled="success" @click="changePassword">
            {{ $t('Change password') }}
          </WdsButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import gql from 'graphql-tag'
import { WdsInput, WdsButton, WdsHeading, WdsAlert, WdsText } from '@worksome/wds-components'
import { ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { $t } from '@/composables/translation'

defineProps({
  status: {
    type: String,
    required: false,
    default: '',
  },
})

const password = ref(null)
const currentPassword = ref(null)
const passwordConfirmation = ref(null)
const success = ref(false)
const statusText = ref('')
const errors = ref({
  password: [],
  currentPassword: [],
  passwordConfirmation: [],
})

const { mutate: changePasswordMutation } = useMutation(
  gql`
    mutation changePassword(
      $currentPassword: String!
      $password: String!
      $passwordConfirmation: String!
    ) {
      updatePassword(
        input: {
          currentPassword: $currentPassword
          password: $password
          passwordConfirmation: $passwordConfirmation
        }
      ) {
        id
      }
    }
  `,
  {
    clientId: 'publicApi',
  },
)

function resetErrors() {
  errors.value = {
    currentPassword: [],
    password: [],
    passwordConfirmation: [],
  }
}
async function changePassword() {
  try {
    await changePasswordMutation({
      currentPassword: currentPassword.value,
      password: password.value,
      passwordConfirmation: passwordConfirmation.value,
    })
    success.value = true
    statusText.value = $t('Your password has been updated.')
  } catch (err: any) {
    resetErrors()
    err.graphQLErrors.forEach((error) => {
      errors.value.currentPassword = errors.value.currentPassword.concat(
        error.extensions.validation.currentPassword ?? [],
      )
      errors.value.password = errors.value.password.concat(
        error.extensions.validation['input.password'] ?? error.extensions.validation.password ?? [],
      )
      errors.value.passwordConfirmation = errors.value.passwordConfirmation.concat(
        error.extensions.validation.passwordConfirmation ?? [],
      )
    })
  }
}
</script>

<style scoped></style>
